import { Archive, CheckCircle, ClipboardList, Eye, TrendingUp, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";

import type { OrderStatusDTO } from "@/common/api";
import {
  useGetMyParticipationOrders,
  useGetTradeOrderStatuses,
  useGetWagonOwnerAggregateData,
} from "@/common/api";
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TruncateTooltip,
  Badge,
} from "@/common/components";
import { Card, CardContent } from "@/common/components/ui/card";
import { ROUTES } from "@/common/config";
import { useUser } from "@/common/hooks";
import { getStatusData } from "@/common/lib";
import { formatDatetime } from "@/common/lib";

const LIMIT = 10;

const getStatusCardData = (status: OrderStatusDTO) => {
  const code = status.code || "";
  switch (code) {
    case "ALL":
      return {
        title: "Всего: участвую в заявках",
        icon: ClipboardList,
        className: "bg-blue-500 text-white",
      };
    case "COLLECT_PROPOSAL":
      return {
        title: "Участвую в заявках: этап Сбор предложений",
        icon: TrendingUp,
        className: "bg-green-500 text-white",
      };
    case "REBIDDING":
      return {
        title: "Участвую в заявках: этап Переторжка",
        icon: Users,
        className: "bg-orange-500 text-white",
      };
    case "SUMMING_UP":
      return {
        title: "Участвую в заявках: этап Подведение итогов",
        icon: CheckCircle,
        className: "bg-purple-500 text-white",
      };
    case "COMPLETED":
      return {
        title: "Участвую в заявках: Завершено",
        icon: Archive,
        className: "bg-gray-500 text-white",
      };
    default:
      return {
        title: status.description || status.code || "",
        icon: ClipboardList,
        className: "bg-gray-400 text-white",
      };
  }
};

export function SobOrdersPage() {
  const { user } = useUser();
  const [, setLocation] = useLocation();

  const [page, setPage] = useState(1);
  const [createdAt, setCreatedAt] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [statusId, setStatusId] = useState("");

  const { data: statusesData } = useGetTradeOrderStatuses();

  const { data, isLoading } = useGetMyParticipationOrders({
    userId: user?.id!,
    statusId: statusId === "" ? undefined : statusId,
    date: createdAt,
    orderNumber: orderNumber ? orderNumber : undefined,
    page: page,
    limit: LIMIT,
  });

  const { data: aggregateData } = useGetWagonOwnerAggregateData({
    userId: user?.id!,
  });

  const totalPages = data?.data.payload?.totalPage;
  const totalItems = data?.data.payload?.totalItems;

  const items = useMemo(() => data?.data.payload?.items ?? [], [data?.data.payload?.items]);
  const statuses = useMemo(() => statusesData?.data.payload ?? [], [statusesData?.data.payload]);
  const aggregateItems = useMemo(
    () => aggregateData?.data.payload ?? [],
    [aggregateData?.data.payload],
  );

  const statsCards = useMemo(() => {
    if (!statuses || statuses.length === 0) return [];

    const totalCount = aggregateItems.find((item) => item.code === "ALL")?.count ?? 0;

    const allStatusCard = {
      id: "",
      code: "ALL",
      description: "Все статусы",
      title: "Всего: заявки",
      icon: ClipboardList,
      className: "bg-blue-500 text-white",
      count: totalCount,
    };

    const statusCards = statuses.map((status) => {
      const aggregateItem = aggregateItems.find((item) => item.code === status.code);
      const cardData = getStatusCardData(status);
      return {
        id: status.id || "",
        code: status.code || "",
        description: status.description || "",
        title: cardData.title,
        icon: cardData.icon,
        className: cardData.className,
        count: aggregateItem?.count ?? 0,
      };
    });

    return [allStatusCard, ...statusCards];
  }, [statuses, aggregateItems]);

  const handleReset = useCallback(() => {
    setStatusId("");
    setCreatedAt("");
    setOrderNumber("");
    setPage(1);
  }, []);

  const handleRowClick = useCallback(
    (itemId: string | undefined) => {
      if (itemId) {
        const basePath = ROUTES.ORDER_ID.replace(":id", String(itemId));
        // При переходе из SobOrdersPage participant в URL всегда true
        setLocation(`${basePath}?participant=1`);
      }
    },
    [setLocation],
  );

  const rows = useMemo(
    () => (
      <>
        {items.map((item) => {
          return (
            <TableRow
              key={item.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => handleRowClick(item.id)}
            >
              <TableCell className="text-center font-medium">{item.orderNumber ?? "-"}</TableCell>
              <TableCell className="text-center">
                {item.tradeBegin && item.tradeEnd ? (
                  <>
                    <p className="text-sm text-nowrap">с {formatDatetime(item.tradeBegin)}</p>
                    <p className="text-sm text-nowrap">по {formatDatetime(item.tradeEnd)}</p>
                  </>
                ) : (
                  <span className="text-sm">-</span>
                )}
              </TableCell>
              <TableCell className="text-center">
                <Badge className={getStatusData(item.status).color}>
                  {getStatusData(item.status).label}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                {item.stageBegin && item.endBegin ? (
                  <>
                    <p className="text-sm text-nowrap">с {formatDatetime(item.stageBegin)}</p>
                    <p className="text-sm text-nowrap">по {formatDatetime(item.endBegin)}</p>
                  </>
                ) : (
                  <span className="text-sm">-</span>
                )}
              </TableCell>
              <TableCell className="text-center">{item.participantsCount ?? "-"}</TableCell>
              <TableCell>
                <TruncateTooltip text={item.cargoName ?? ""} className="text-sm font-medium" />
                <p className="text-sm">({item.cargoWeight} т.)</p>
              </TableCell>

              <TableCell>
                {item.wagonsType && item.totalWagons && (
                  <>
                    <p className="text-sm font-medium">{item.wagonsType}</p>
                    <p className="text-sm">({item.totalWagons} в.)</p>
                  </>
                )}
              </TableCell>
              <TableCell className="flex justify-center">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick(item.id);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </>
    ),
    [items, setLocation, handleRowClick],
  );

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: "var(--color-primary)" }}>
          Личный кабинет собственника вагонов
        </h2>
        <p className="text-lg font-medium text-gray-700 mt-2">
          Мои участия по заявкам-предложениям
        </p>
      </div>

      {/* Statuses */}
      <div className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 md:gap-4 mb-6">
          {statsCards.map((stat) => {
            const Icon = stat.icon;
            const isActive = statusId === stat.id;
            return (
              <button
                onClick={() => {
                  setPage(1);
                  setStatusId(stat.id);
                }}
                key={stat.id || "all"}
                aria-pressed={isActive}
              >
                <Card
                  className={`cursor-pointer transition-shadow h-28 md:h-32 md:p-3 p-4 overflow-hidden ${
                    isActive ? "shadow-md ring-1 ring-zinc-200 border-gray-400" : "hover:shadow-md"
                  }`}
                >
                  <CardContent className="h-full text-center flex flex-col justify-between">
                    <div
                      className={`p-2 rounded-full mx-auto mb-2 w-8 h-8 flex items-center justify-center ${stat.className}`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className="text-[0.625rem] md:text-xs font-normal text-Text-secondary mb-1 leading-3 md:leading-4">
                      {stat.title}
                    </p>
                    <p className="text-md md:text-lg font-bold text-zinc-800 leading-4 md:leading-6">
                      {stat.count || "-"}
                    </p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-Text-main leading-4 mb-1">
                Дата создания
              </label>
              <Input
                key="createdAt"
                type="date"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                placeholder="ДД.ММ.ГГГГ"
                className="block"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-Text-main leading-4 mb-1">
                Номер заявки
              </label>
              <Input
                placeholder="Введите № заявки"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" className="w-full" onClick={handleReset}>
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="mt-6">
        <Card>
          <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
            {isLoading ? (
              <div className="text-center py-8">
                <p className="caption">Загрузка заявок...</p>
              </div>
            ) : data?.data.payload?.items?.length === 0 ? (
              <div className="text-center py-8">
                <p className="caption">Нет заявок</p>
              </div>
            ) : (
              <Table className="">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-start text-xs font-semibold leading-4 text-Text-secondary">
                      Номер заявки
                    </TableHead>

                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      Период проведения
                    </TableHead>

                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      Этап
                    </TableHead>

                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      Период этапа
                    </TableHead>

                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      Количество участников
                    </TableHead>

                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      Груз
                    </TableHead>

                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      Вагоны
                    </TableHead>

                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      Действия
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{rows}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <TablePagination
          currentPage={page}
          totalPages={totalPages ?? 0}
          totalItems={totalItems ?? 0}
          pageSize={LIMIT}
          onPageChange={(page) => {
            setPage(page);
          }}
        />
      </div>
    </>
  );
}
